'use strict';

const { GAME_WIDTH, GAME_HEIGHT, COLORS } = require('../config/constants');

class Renderer {
  constructor(canvas) {
    if (!canvas) throw new Error('A canvas element is required');
    this.canvas = canvas;
    this.ctx = this.canvas.getContext('2d');
    this.screenWidth = GAME_WIDTH;
    this.screenHeight = GAME_HEIGHT;
    this.pixelRatio = 1;
    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.safeTop = 24;
    this.safeBottom = GAME_HEIGHT - 24;
    this.stars = this.createStars(72);
    this.resize();
  }

  createStars(count) {
    const stars = [];
    let seed = 20260730;
    const random = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    };
    for (let i = 0; i < count; i += 1) {
      stars.push({
        x: random() * GAME_WIDTH,
        y: random() * GAME_HEIGHT,
        radius: 0.8 + random() * 2.2,
        speed: 5 + random() * 18,
        alpha: 0.25 + random() * 0.65
      });
    }
    return stars;
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    this.screenWidth = rect.width || window.innerWidth || GAME_WIDTH;
    this.screenHeight = rect.height || window.innerHeight || GAME_HEIGHT;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 3);
    this.scale = Math.min(this.screenWidth / GAME_WIDTH, this.screenHeight / GAME_HEIGHT);
    this.offsetX = (this.screenWidth - GAME_WIDTH * this.scale) / 2;
    this.offsetY = (this.screenHeight - GAME_HEIGHT * this.scale) / 2;
    this.canvas.width = Math.floor(this.screenWidth * this.pixelRatio);
    this.canvas.height = Math.floor(this.screenHeight * this.pixelRatio);

    const viewport = window.visualViewport;
    const safeTop = viewport ? Math.max(0, viewport.offsetTop) : 0;
    const safeBottom = viewport
      ? Math.min(this.screenHeight, viewport.offsetTop + viewport.height)
      : this.screenHeight;
    this.safeTop = Math.max(24, (safeTop - this.offsetY) / this.scale + 12);
    this.safeBottom = Math.min(
      GAME_HEIGHT - 24,
      (safeBottom - this.offsetY) / this.scale - 12
    );
  }

  beginFrame() {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = COLORS.backgroundBottom;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    const factor = this.pixelRatio * this.scale;
    ctx.setTransform(
      factor,
      0,
      0,
      factor,
      this.offsetX * this.pixelRatio,
      this.offsetY * this.pixelRatio
    );
  }

  drawBackground(timeSeconds) {
    const ctx = this.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, GAME_HEIGHT);
    gradient.addColorStop(0, COLORS.backgroundTop);
    gradient.addColorStop(0.55, '#071127');
    gradient.addColorStop(1, COLORS.backgroundBottom);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    const time = timeSeconds || 0;
    for (let i = 0; i < this.stars.length; i += 1) {
      const star = this.stars[i];
      const y = (star.y + time * star.speed) % GAME_HEIGHT;
      const pulse = 0.65 + Math.sin(time * 1.5 + i) * 0.25;
      ctx.globalAlpha = Math.max(0.1, star.alpha * pulse);
      ctx.fillStyle = i % 9 === 0 ? COLORS.cyan : '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x, y, star.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  toLogicalPoint(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - this.offsetX) / this.scale,
      y: (clientY - rect.top - this.offsetY) / this.scale
    };
  }

  clientDeltaToLogical(dx, dy) {
    const rect = this.canvas.getBoundingClientRect();
    const renderedWidth = Math.max(1, Math.min(rect.width, rect.height * (GAME_WIDTH / GAME_HEIGHT)));
    const renderedHeight = Math.max(1, Math.min(rect.height, rect.width * (GAME_HEIGHT / GAME_WIDTH)));
    return {
      x: Number(dx || 0) * GAME_WIDTH / renderedWidth,
      y: Number(dy || 0) * GAME_HEIGHT / renderedHeight
    };
  }

  getBounds() {
    return {
      left: 0,
      right: GAME_WIDTH,
      top: this.safeTop,
      bottom: this.safeBottom
    };
  }
}

module.exports = Renderer;
